import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, FileText, User, LogOut, Settings, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const navigation = [
    { nameKey: 'common.navTemplates', href: '/templates' },
    { nameKey: 'common.navExamples', href: '/examples' },
    { nameKey: 'common.navPricing', href: '/pricing' },
    { nameKey: 'common.navHelp', href: '/help' },
  ];

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleUserMenu = () => setIsUserMenuOpen(!isUserMenuOpen);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <FileText className="w-8 h-8 text-primary-600" />
            <span className="text-2xl font-bold text-gray-900">
              {t('common.appName')}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-gray-600 hover:text-primary-600 transition-colors duration-200 ${
                  location.pathname === item.href ? 'text-primary-600' : ''
                }`}
              >
                {t(item.nameKey)}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth + Language */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Language toggle */}
            <button
              onClick={toggleLanguage}
              className="px-3 py-1 rounded-full border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {language === 'en' ? 'ES' : 'EN'}
            </button>

            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center space-x-3 text-gray-700 hover:text-primary-600 transition-colors duration-200 p-2 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-2">
                    {user?.profile_picture ? (
                      <img
                        src={user.profile_picture}
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-primary-600" />
                      </div>
                    )}
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.first_name || user?.username || 'User'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {user?.is_company || user?.user_type === 'company'
                          ? t('common.company')
                          : t('common.candidate')}
                      </p>
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                    >
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      
                      <div className="py-1">
                        <Link
                          to={
                            (user?.is_company || user?.user_type === 'company') 
                              ? "/company-dashboard" 
                              : (user?.is_candidate || user?.user_type === 'candidate')
                              ? "/candidate-dashboard"
                              : "/dashboard"
                          }
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <FileText className="w-4 h-4 mr-3" />
                          Dashboard
                        </Link>
                        
                        {(user?.is_candidate || user?.user_type === 'candidate') && (
                          <Link
                            to="/profile/edit"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <Settings className="w-4 h-4 mr-3" />
                            Edit Profile
                          </Link>
                        )}
                      </div>
                      
                      <div className="border-t border-gray-100 py-1">
                        <button 
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-4 h-4 mr-3" />
                          Logout
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <>
                <Link to="/login" className="text-gray-600 hover:text-primary-600 transition-colors duration-200">
                  {t('common.login')}
                </Link>
                <Link to="/register" className="btn-primary">
                  {t('common.signup')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 rounded-md text-gray-600 hover:text-primary-600 hover:bg-gray-100 transition-colors duration-200"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              ref={mobileMenuRef}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-gray-200 py-4"
            >
              <nav className="flex flex-col space-y-4">
                {/* Mobile language toggle */}
                <button
                  onClick={toggleLanguage}
                  className="self-end mb-2 inline-flex items-center px-3 py-1 rounded-full border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('common.languageToggleLabel')}: {language === 'en' ? t('common.languageSpanish') : t('common.languageEnglish')}
                </button>
                {navigation.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`text-gray-600 hover:text-primary-600 transition-colors duration-200 ${
                      location.pathname === item.href ? 'text-primary-600' : ''
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t(item.nameKey)}
                  </Link>
                ))}
                
                {isAuthenticated ? (
                  <>
                    <div className="flex items-center space-x-3 py-2 border-b border-gray-200 mb-4">
                      {user?.profile_picture ? (
                        <img
                          src={user.profile_picture}
                          alt="Profile"
                          className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <User className="w-6 h-6 text-primary-600" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user?.is_company || user?.user_type === 'company'
                            ? t('common.company')
                            : t('common.candidate')}
                        </p>
                      </div>
                    </div>
                    
                    <Link
                      to={
                        (user?.is_company || user?.user_type === 'company') 
                          ? "/company-dashboard" 
                          : (user?.is_candidate || user?.user_type === 'candidate')
                          ? "/candidate-dashboard"
                          : "/dashboard"
                      }
                      className="flex items-center text-gray-600 hover:text-primary-600 transition-colors duration-200 py-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <FileText className="w-4 h-4 mr-3" />
                      Dashboard
                    </Link>
                    
                    {(user?.is_candidate || user?.user_type === 'candidate') && (
                      <Link
                        to="/profile/edit"
                        className="flex items-center text-gray-600 hover:text-primary-600 transition-colors duration-200 py-2"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4 mr-3" />
                      {t('common.editProfile')}
                      </Link>
                    )}
                    
                    <button 
                      onClick={handleLogout}
                      className="flex items-center text-left text-red-600 hover:text-red-700 transition-colors duration-200 py-2"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      {t('common.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="text-gray-600 hover:text-primary-600 transition-colors duration-200"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('common.login')}
                    </Link>
                    <Link
                      to="/register"
                      className="btn-primary text-center"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('common.signup')}
                    </Link>
                  </>
                )}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
